import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { SvgFilter } from '@devsbb/ui';

import { tk } from '@/i18n/translationKeys';
import { trackYourPaymentsPageVisitedEvent, TrackYourPaymentsEvents } from '@/analytics';
import { useScreenMatch } from '@/hooks/useScreenMatch';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useApplicationData } from '@/providers/applicationData';
import { getLocale } from '@/utils/cookies';
import { LoadingUnit } from '@/components/Loading';
import { DisplayIf } from '@/components/DisplayIf';
import { Page } from '@/components/Page';
import { DocumentTitle, PageName } from '@/components/DocumentTitle';
import { Text } from '@/components/Text';
import { Wrapper } from '@/components/Wrapper';
import { ArrowLeftIcon } from '@/icons/ArrowLeftIcon';
import { PaymentState, PaymentEnum, PaymentsUnion, SubscriptionPayment } from '@/generated/types';
import { TopPane } from './TopPane';
import { LinkToPaymentMethods } from './LinkToPaymentMethods';
import { FilterPane } from './FilterPane';
import { PaymentsList } from './PaymentsList';
import { usePaymentsList, UsePaymentsListResult } from './PaymentsList/usePaymentsList';
import { FiltersScreen } from './FiltersScreen';
import { FailedBanner } from './FailedBanner';
import { FailedBannerPreloader } from './FailedBanner/FailedBannerPreloader';
import { FilterType } from './types';
import { PaymentInProgressModal } from './PaymentInProgressModal';

import { Root, BackButton, FilterButton } from './YourPaymentsPage.styles';
import { DEFAULT_LANGUAGE, QueryParamName } from '@/constants';
import { isInWebView } from '@/modules/webView';

const SHOW_FAILED_BANNER_WARNING_KEY = 'show-failed-banner-warning';
const PAYMENT_IN_PROGRESS_MODAL_DISMISSED_KEY = 'payment-in-progress-modal-dismissed';

const ActualFailedBanner = ({
  failedSubscriptionPaymentsListResult,
  failedSubscriptionCount,
  failedSubscriptionPrice,
  failedSubscriptionPayments,
  isWarning,
}: {
  failedSubscriptionPaymentsListResult: UsePaymentsListResult;
  failedSubscriptionCount: number;
  failedSubscriptionPrice: string | null;
  failedSubscriptionPayments: SubscriptionPayment[];
  isWarning: boolean;
}) => {
  if (failedSubscriptionPaymentsListResult.loading) {
    return <FailedBannerPreloader />;
  }

  if (failedSubscriptionCount > 0) {
    return (
      <FailedBanner
        totalCount={failedSubscriptionCount}
        totalPrice={failedSubscriptionPrice}
        paymentsList={failedSubscriptionPayments}
        variant={isWarning ? 'warning' : 'error'}
      />
    );
  }
  return null;
};

const ActualFilterButton = ({
  paymentsListResult,
  filterValueCount,
  handleFilterClick,
}: {
  paymentsListResult: UsePaymentsListResult;
  filterValueCount: number;
  handleFilterClick: () => void;
}) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams();
  const { subscriptionId } = queryParams;

  if (subscriptionId) {
    return null;
  }
  if (paymentsListResult.loading && !filterValueCount) {
    return <LoadingUnit width={21} height={8} />;
  }
  return (
    <FilterButton size="S" variant="Primary" onClick={handleFilterClick}>
      <SvgFilter />
      {filterValueCount
        ? `${t(tk.yourPaymentsFilterChipTitle)} (${filterValueCount})`
        : `${t(tk.yourPaymentsFilterChipTitle)}`}
    </FilterButton>
  );
};

const ActualFilter = ({
  filterValueCount,
  selectedFilters,
  setSelectedFilters,
  paymentsListResult,
  handleFilterClick,
}: {
  filterValueCount: number;
  selectedFilters: FilterType[];
  setSelectedFilters: React.Dispatch<React.SetStateAction<FilterType[]>>;
  paymentsListResult: UsePaymentsListResult;
  handleFilterClick: () => void;
}) => {
  const { flags } = useApplicationData();
  if (!flags.pmmp_filter_your_payments) {
    return null;
  }

  return (
    <>
      <DisplayIf desktop>
        <FilterPane
          filterValueCount={filterValueCount}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
        />
      </DisplayIf>
      <DisplayIf mobile>
        <ActualFilterButton
          paymentsListResult={paymentsListResult}
          filterValueCount={filterValueCount}
          handleFilterClick={handleFilterClick}
        />
      </DisplayIf>
    </>
  );
};

export const YourPaymentsPage = (): JSX.Element => {
  const queryParams = useQueryParams();
  const { subscriptionId, device, status, paymentType } = queryParams;
  const selectedTypes: FilterType[] = Object.entries(queryParams)
    .filter(([key, value]) => Boolean(value) && key !== 'subscriptionId')
    .map(([key]) => key as FilterType);
  const [selectedFilters, setSelectedFilters] = useState<FilterType[]>(selectedTypes);
  const [showFiltersScreen, setShowFiltersScreen] = useState<boolean>(false);
  const [showWarningBanner, setShowWarningBanner] = useState<boolean>(
    localStorage.getItem(SHOW_FAILED_BANNER_WARNING_KEY) === 'true'
  );
  const [searchParams] = useSearchParams();
  const shouldShowPaymentInProgressModal = !!searchParams.get(
    QueryParamName.showPaymentInProgressModal
  );

  const isPaymentInProgress: boolean =
    shouldShowPaymentInProgressModal &&
    localStorage.getItem(PAYMENT_IN_PROGRESS_MODAL_DISMISSED_KEY) !== 'true';
  const [showPaymentInProgressModal, setShowPaymentInProgressModal] =
    useState<boolean>(isPaymentInProgress);

  const { t } = useTranslation();
  const { isMobile } = useScreenMatch();
  const { userId, activeStore, storeCode, flags, routeBuilder } = useApplicationData();
  const navigate = useNavigate();
  const locale = getLocale() || DEFAULT_LANGUAGE;
  const theme = useTheme();

  const queryParamsFilterValues = [device, status, paymentType];

  const isFilterEmptyResult =
    (status === PaymentState.FAILED && paymentType === PaymentEnum.ORDER_PAYMENT) ||
    (status === 'CANCELED' && paymentType && paymentType !== PaymentEnum.ORDER_PAYMENT) ||
    (status === 'CANCELED' && device);

  const paymentsListResult = usePaymentsList({
    subscriptionId,
    device,
    status,
    paymentType,
    isFilterEmptyResult: !!isFilterEmptyResult,
  });

  const failedSubscriptionPaymentsListResult = usePaymentsList({
    subscriptionId,
    device,
    status: PaymentState.FAILED,
    paymentType: PaymentEnum.SUBSCRIPTION_PAYMENT,
    isFilterEmptyResult: false,
    isForFailedBanner: true,
  });

  const { failedSubscriptionCount, failedSubscriptionPrice, failedSubscriptionPayments } =
    useMemo(() => {
      const failedSubscriptionCount = failedSubscriptionPaymentsListResult.data?.totalCount ?? 0;
      const failedSubscriptionPrice =
        failedSubscriptionPaymentsListResult.data?.formattedTotalPrice ?? null;
      const isSubscriptionPayment = (payment: PaymentsUnion): payment is SubscriptionPayment => {
        return payment.__typename === 'SubscriptionPayment';
      };
      const failedSubscriptionPayments = (
        failedSubscriptionPaymentsListResult.data?.payments ?? []
      ).filter(isSubscriptionPayment);

      return {
        failedSubscriptionCount,
        failedSubscriptionPrice,
        failedSubscriptionPayments,
      };
    }, [failedSubscriptionPaymentsListResult.data]);

  const filterValueCount = queryParamsFilterValues.filter((value) => Boolean(value)).length;

  useEffect(() => {
    trackYourPaymentsPageVisitedEvent({
      path: location.pathname,
      store: storeCode,
      store_id: activeStore.store_id,
      locale,
    });
  }, []);

  const handleBackClick = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      location.href = routeBuilder.buildYourProfileRoute();
    }
  }, [navigate, routeBuilder]);

  const handleFilterClick = () => {
    setShowFiltersScreen(true);
    TrackYourPaymentsEvents.filterChipClick({
      userId,
      store: storeCode,
    });
  };

  const handleClosePaymentInProgressModal = () => {
    localStorage.setItem(PAYMENT_IN_PROGRESS_MODAL_DISMISSED_KEY, 'true');
    localStorage.setItem(SHOW_FAILED_BANNER_WARNING_KEY, 'true');
    setShowWarningBanner(true);
    setShowPaymentInProgressModal(false);
  };

  // refresh won’t keep showing warning banner
  useEffect(() => {
    if (showWarningBanner) {
      localStorage.removeItem(SHOW_FAILED_BANNER_WARNING_KEY);
    }
  }, [showWarningBanner]);

  return showFiltersScreen && isMobile && flags.pmmp_filter_your_payments ? (
    <FiltersScreen
      totalCount={filterValueCount ? paymentsListResult?.data?.totalCount : null}
      paymentsListLoading={paymentsListResult?.loading}
      setShowFiltersScreen={setShowFiltersScreen}
    />
  ) : (
    <Page>
      <DocumentTitle pageName={PageName.yourPayments} />
      <Root isMobile={isMobile}>
        <DisplayIf desktop>
          <ActualFailedBanner
            failedSubscriptionPaymentsListResult={failedSubscriptionPaymentsListResult}
            failedSubscriptionCount={failedSubscriptionCount}
            failedSubscriptionPrice={failedSubscriptionPrice}
            failedSubscriptionPayments={failedSubscriptionPayments}
            isWarning={showWarningBanner}
          />
          <Wrapper height="64px" justifyContent="space-between" margin="16px 0">
            <Wrapper>
              <Text
                typography="SmallHeadline"
                color={theme.colors.text.darkerGrey}
                data-testid="your-payments-page-main-title"
              >
                {t(tk.yourPaymentsPageMainTitle)}
              </Text>
            </Wrapper>

            <LinkToPaymentMethods />
          </Wrapper>
        </DisplayIf>

        <DisplayIf mobile>
          <Wrapper height="64px" justifyContent="space-between" margin="0 0 8px">
            <Wrapper>
              {isInWebView() ? null : (
                <BackButton onClick={handleBackClick} data-testid="back-button">
                  <ArrowLeftIcon />
                </BackButton>
              )}

              <Text
                typography="SuperTinyHeadline"
                color={theme.colors.text.darkerGrey}
                data-testid="your-payments-page-main-title"
              >
                {t(tk.yourPaymentsPageMainTitle)}
              </Text>
            </Wrapper>

            <LinkToPaymentMethods />
          </Wrapper>
          <ActualFailedBanner
            failedSubscriptionPaymentsListResult={failedSubscriptionPaymentsListResult}
            failedSubscriptionCount={failedSubscriptionCount}
            failedSubscriptionPrice={failedSubscriptionPrice}
            failedSubscriptionPayments={failedSubscriptionPayments}
            isWarning={showWarningBanner}
          />
        </DisplayIf>

        <TopPane />

        <ActualFilter
          filterValueCount={filterValueCount}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          paymentsListResult={paymentsListResult}
          handleFilterClick={handleFilterClick}
        />

        <PaymentsList
          paymentsListResult={paymentsListResult}
          filterValueCount={filterValueCount}
          setSelectedFilters={setSelectedFilters}
        />

        {showPaymentInProgressModal ? (
          <PaymentInProgressModal handleCloseModal={handleClosePaymentInProgressModal} />
        ) : null}
      </Root>
    </Page>
  );
};
